"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/PlaidConnectFlow/hooks/usePlaidManualUpload";

import { useCallback, useMemo, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { OspToast } from "@ospweb/sdk-ui";
import { Track } from "@/src/lib/tracking";
import {
  uploadAttachmentFiles,
  type UploadedAttachmentFile,
} from "@/src/utils/attachmentUpload/uploadAttachmentFiles";
import { resolvePortfolioUploadAuthHeaders } from "@/src/hooks/onboarding/portfolio/upload/resolveUploadAuthHeaders";
import type { SenderAttachment } from "@components/Sender/type";
import { rootStore } from "@store/rootStore";
import type { PlaidUploadItem } from "../shared/PlaidUploadedDocumentChip";
import log from "logbeacon";

const I18n = I18nUtil.createScoped(Translations);

export const PLAID_MAX_MANUAL_FILES = 10;
export const PLAID_MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB

const ALLOWED_EXTENSIONS = [
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "webp",
  "csv",
  "xlsx",
  "xls",
];

const ALLOWED_MIME_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

function isFileTypeSupported(file: File): boolean {
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  if (ALLOWED_EXTENSIONS.includes(ext)) {
    return true;
  }
  if (file.type && ALLOWED_MIME_TYPES.includes(file.type)) {
    return true;
  }
  return false;
}

export interface UsePlaidManualUploadReturn {
  rawFiles: PlaidUploadItem[];
  uploadedFiles: SenderAttachment[];
  isUploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  openFilePicker: () => void;
  handleFileInputChange: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void>;
  handlePickFiles: (files: FileList | File[]) => Promise<boolean>;
  removeFile: (id: string) => void;
  clearFiles: () => void;
}

export function usePlaidManualUpload(
  initialFiles: UploadedAttachmentFile[] = [],
): UsePlaidManualUploadReturn {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { fingerprint } = useSnapshot(rootStore.user);
  const [items, setItems] = useState<PlaidUploadItem[]>(() =>
    initialFiles.map((file, idx) => ({
      ...file,
      id: `init_${idx}_${file.filename}`,
      status: "done",
    })),
  );

  const isUploading = useMemo(
    () => items.some((item) => item.status === "uploading"),
    [items],
  );

  const uploadedFiles = useMemo(
    () =>
      items
        .filter((item) => item.status === "done" && Boolean(item.url))
        .map(({ filename, mediaType, url, size }) => ({
          type: "file" as const,
          filename,
          ...(mediaType ? { mediaType } : {}),
          url,
          ...(size != null ? { size } : {}),
        })),
    [items],
  );

  const openFilePicker = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  }, []);

  const removeFile = useCallback((id: string) => {
    setItems((prev) => {
      const removed = prev.find((item) => item.id === id);
      if (removed) {
        Track.event("plaid_manual_upload_remove", {
          filename: removed.filename,
        });
      }
      return prev.filter((item) => item.id !== id);
    });
  }, []);

  const clearFiles = useCallback(() => {
    setItems([]);
  }, []);

  const handlePickFiles = useCallback(
    async (picked: FileList | File[]): Promise<boolean> => {
      const fileArray = Array.from(picked);
      if (fileArray.length === 0) {
        return false;
      }

      Track.event("plaid_manual_upload_pick", {
        count: fileArray.length,
      });

      // 1. Filter supported types
      const unsupported = fileArray.filter(
        (file) => !isFileTypeSupported(file),
      );
      if (unsupported.length > 0) {
        OspToast.error({
          content: I18n.t(
            "Only PDF, image (PNG/JPG/WEBP), or CSV/Excel files are supported.",
          ),
        });
      }
      let validFiles = fileArray.filter((file) => isFileTypeSupported(file));
      if (validFiles.length === 0) {
        return false;
      }

      // 2. Filter oversized files (<= 20MB)
      const oversized = validFiles.filter(
        (file) => file.size > PLAID_MAX_FILE_SIZE_BYTES,
      );
      if (oversized.length > 0) {
        OspToast.error({
          content: I18n.t("File size exceeds 20MB limit."),
        });
      }
      validFiles = validFiles.filter(
        (file) => file.size <= PLAID_MAX_FILE_SIZE_BYTES,
      );
      if (validFiles.length === 0) {
        return false;
      }

      // 3. Count limits (<= 10)
      const currentCount = items.length;
      const remaining = PLAID_MAX_MANUAL_FILES - currentCount;
      if (remaining <= 0) {
        OspToast.error({
          content: I18n.t("You can upload up to %{var0} documents.", {
            var0: PLAID_MAX_MANUAL_FILES,
          }),
        });
        return false;
      }
      if (validFiles.length > remaining) {
        OspToast.info({
          content: I18n.t(
            "Only the first %{var0} document(s) were added due to limit.",
            {
              var0: remaining,
            },
          ),
        });
        validFiles = validFiles.slice(0, remaining);
      }

      // 4. Create batch items with uploading state
      const batchId = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const newItems: PlaidUploadItem[] = validFiles.map((file, idx) => ({
        id: `${batchId}_${idx}`,
        filename: file.name,
        size: file.size,
        url: "",
        status: "uploading",
        mediaType: file.type || undefined,
      }));

      setItems((prev) => [...prev, ...newItems]);

      // 5. Upload via API
      try {
        const uploadAuthHeaders = await resolvePortfolioUploadAuthHeaders({});

        const uploadedResults = await uploadAttachmentFiles({
          files: validFiles,
          authHeaders: uploadAuthHeaders,
          fingerprint,
        });

        let resultIdx = 0;
        const itemUpdates: Record<string, Partial<PlaidUploadItem>> = {};
        let batchHadFailure = false;
        for (const item of newItems) {
          const res = uploadedResults[resultIdx++];
          if (res?.url) {
            itemUpdates[item.id] = {
              filename: res.filename || item.filename,
              size: res.size ?? item.size,
              url: res.url,
              mediaType: res.mediaType || item.mediaType,
              status: "done",
            };
          } else {
            batchHadFailure = true;
            itemUpdates[item.id] = { status: "error" };
          }
        }

        setItems((prev) =>
          prev.map((item) =>
            itemUpdates[item.id] ? { ...item, ...itemUpdates[item.id] } : item,
          ),
        );

        if (batchHadFailure) {
          OspToast.error({
            content: I18n.t("Upload failed. Please try again."),
          });
        }

        const successResults = uploadedResults.filter((r) => Boolean(r?.url));
        if (successResults.length > 0) {
          Track.event("plaid_manual_upload_success", {
            count: successResults.length,
            files: successResults.map((r) => r.filename).join(", "),
          });
        }
        return !batchHadFailure;
      } catch (error) {
        log.error("[文案]Manual document upload failed:", error);
        OspToast.error({ content: I18n.t("Upload failed. Please try again.") });
        setItems((prev) =>
          prev.map((item) =>
            item.id.startsWith(batchId) && item.status === "uploading"
              ? { ...item, status: "error" as const }
              : item,
          ),
        );
        return false;
      }
    },
    [fingerprint, items.length],
  );

  const handleFileInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        await handlePickFiles(files);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [handlePickFiles],
  );

  return {
    rawFiles: items,
    uploadedFiles,
    isUploading,
    fileInputRef,
    openFilePicker,
    handleFileInputChange,
    handlePickFiles,
    removeFile,
    clearFiles,
  };
}
